import { Banner, DataTable, Text } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { useUpdateEffect } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { createSearchUrlParams } from 'app/views/layout/views/search/utils';
import { AppLink } from 'components/appLink';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { useUserQueues } from 'utils/user/useUserQueues';

import styles from '../../userDetails.module.scss';
import { makeColumns } from '../../utils/makeColumns';

const conversationsLoading = (
  <Text.Paragraph>Loading conversations...</Text.Paragraph>
);

const conversationsFailed = (
  <Banner
    variant="fail"
    icon={null}
    description={
      <Text.Paragraph>
        An error occurred while loading conversations
      </Text.Paragraph>
    }
  />
);

const conversationsEmpty = (
  <Banner
    variant="info"
    icon={null}
    description={
      <Text.Paragraph>There are no previous conversations</Text.Paragraph>
    }
  />
);

export type UserConversationsProps = {
  user: User;
};

export function UserConversations({
  user,
}: UserConversationsProps): JSX.Element {
  const {
    items,
    total,
    data,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    changeArg,
  } = usePaginatedEndpoint({
    endpoint: conversationsApi.endpoints.readConversations,
    itemsSelector: ({ data: { conversations } }) => conversations,
    totalSelector: ({ data: { conversations_total_count } }) =>
      conversations_total_count || 0,
    initialArg: {
      requesterIds: [user.id],
      limit: 6,
      offset: 0,
      showRestrictedCount: true,
    },
  });

  const userQueues = useUserQueues();

  useUpdateEffect(() => {
    changeArg({
      requesterIds: [user.id],
      limit: 6,
      offset: 0,
    });
  }, [user.id, changeArg]);

  if (asyncStatus === 'rejected') {
    return conversationsFailed;
  }

  if (
    userQueues.asyncStatus === 'pending' ||
    userQueues.asyncStatus === 'uninitialized'
  ) {
    return conversationsLoading;
  }

  if (!items.length) {
    if (asyncStatus === 'pending') {
      return conversationsLoading;
    }

    return conversationsEmpty;
  }

  return (
    <>
      {data.conversations_restricted_count ? (
        <Banner
          variant="warning"
          description={
            <Text.Paragraph>{`${data.conversations_restricted_count} conversations in restricted queues excluded`}</Text.Paragraph>
          }
          className={styles.restrictedHitsBanner}
        />
      ) : null}
      <DataTable
        data={items}
        columns={makeColumns(
          ({ conversation }) => conversation.assignment?.agent,
          'Assigned to',
          userQueues,
        )}
        paginationProps={{
          loading: asyncStatus === 'pending',
          previousButtonDisabled: !hasPreviousPage,
          nextButtonDisabled: !hasNextPage,
          previousButtonClick: previousPage,
          nextButtonClick: nextPage,
        }}
      />

      <div className={styles.seeAllLinkContainer}>
        <AppLink
          path="/search/:scope"
          params={createSearchUrlParams({
            scope: 'conversations',
            query: '',
            filters: {
              strategy: 'all',
              conditions: [
                {
                  field: 'requested_by',
                  operator: 'is_one',
                  values: [{ label: '', value: user.id }],
                },
              ],
            },
          })}
          onClick={() => ampli.contactsCardSeeAllConversationsClicked()}
        >
          {`See all ${total ? `${total} ` : ''}${
            data.conversations_restricted_count != null &&
            data.conversations_restricted_count > 0
              ? 'unrestricted '
              : ''
          }conversations`}
        </AppLink>
      </div>
    </>
  );
}
